import React, {useState, useEffect, useRef} from 'react';
import {useForm} from 'react-hook-form';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTitle} from '../../../components/General/Layout';
import LoadingWheel from '../../../components/General/LoadingWheel';
import MaintenanceHeader from '../../../components/Surveys/SurveyMaintenanceHeader';
import ErrorHelper from '../../../service/ErrorHelper';
import {SMaster, Survey} from '../../../service/Urls';
import {get, post} from '../../../service/ApiService';
import SatisfactionSection from '../../../components/Surveys/SatisfactionSection';
import {Box, Flex, SimpleGrid, Spacer, Text, Textarea} from '@chakra-ui/react';
import SurveySideBar from '../../../components/Surveys/SurveySideBar';
import FlexCard from '../../../components/General/FlexCard';
import SatisfactionToTen from '../../../components/Surveys/SatisfactionToTen';
import {CustomCheckbox, CustomInput, CustomIntRadioGroup} from '../../../components/General/FormInputs';
import BoxCard from '../../../components/General/BoxCard';
import {formatDate} from '../../../helpers/Functions';
import PreArrangeRadio from '../../../components/Surveys/PreArrangeRadio';
import {Satisfaction, SatisfactionOneToTen} from '../../../constants/Satisfaction';

// Satisfaction question fields only (excludes sv99a recommendation, sv10a pre-arrange, sv11a brochure)
const CREMATION_SATISFACTION_IDS = [
  'sv3a', 'sv3b', 'sv3c', 'sv3d',
  'sv4a', 'sv4b', 'sv4c', 'sv4d', 'sv4e', 'sv4f', 'sv4g', 'sv4h',
  'sv6k',
  'sv7a', 'sv8a',
];

const isNegativeRecommendation = value =>
  value != null &&
  value >= SatisfactionOneToTen.SIX &&
  value <= SatisfactionOneToTen.ZERO;

const CremationMaintenance = () => {
  const [data, setData] = useState({});
  const [smasterData, setSmasterData] = useState({});
  const [toast, setToast] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdate, setIsUpdate] = useState(true);
  const [ids, setIds] = useState([]);
  const [title, setTitle] = useTitle('Cremation Survey Maintenance');

  const isMounted = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();
  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    setValue,
    formState: {errors},
  } = useForm();

  useEffect(() => {
    isMounted.current = true;

    loadData(location?.state?.id);

    return () => {
      isMounted.current = false;
    };
  }, []);

  const loadData = async masterId => {
    const data = await get(Survey.GET + `/` + masterId);
    setToast(data);
    if (!data.error) {
      if (!data.dateAdded) {
        data.dateAdded = localStorage.getItem('prevDateAdded') || new Date().toISOString().split('T')[0];
        setIsUpdate(false);
      } else {
        // For a datepicker to default a value it must be in yyyy-mm-dd.  Modify initial value then call reset on the data to get that defaulted
        data.dateAdded = data.dateAdded.split('T')[0];
      }
      // Remove sv11d (Address 2) from API response to keep it empty
      const {sv11d, ...dataWithoutSv11d} = data;
      setData(dataWithoutSv11d);
      reset(dataWithoutSv11d);
      // Explicitly set sv11d (Address 2) to empty string on page load
      setValue('sv11d', '');
      const smasterResponse = await get(SMaster.RECORD + '/' + masterId);
      setToast(smasterResponse);
      if (!smasterResponse.error) {
        smasterResponse['contractDate'] = formatDate(smasterResponse.smCondate);
        setSmasterData(smasterResponse);
      }
    }
    setIsLoading(false);
    setTitle('Cremation Survey Maintenance');
    assignIds();
  };

  // ids for the good/excellent/na questions for the buttons to auto assign
  const assignIds = () => {
    let arr = [];
    const prompts = [prompt3, prompt4, promptSupport, prompt7];
    prompts.forEach(promptArr => {
      arr = arr.concat(promptArr.map(prompt => prompt.id));
    });
    setIds(arr);
  };

  // Function to check if Negative Survey should be automatically selected
  const shouldAutoSelectNegativeSurvey = (formValues) => {
    if (!formValues) return false;

    // Only evaluate satisfaction questions; N/A (value 5) is neutral and excluded
    const poorSelected = CREMATION_SATISFACTION_IDS.some(
      id => formValues[id] === Satisfaction.POOR,
    );
    if (poorSelected) return true;

    const fairCount = CREMATION_SATISFACTION_IDS.filter(
      id => formValues[id] === Satisfaction.FAIR,
    ).length;
    if (fairCount >= 3) return true;

    // Recommendation ratings 0–6 only; N/A (value 12) does not trigger negative
    if (isNegativeRecommendation(formValues.sv99a)) return true;

    return false;
  };

  // Function to check if Negative Survey should be automatically unchecked
  const shouldAutoUncheckNegativeSurvey = (formValues) => {
    if (!formValues) return false;

    const poorSelected = CREMATION_SATISFACTION_IDS.some(
      id => formValues[id] === Satisfaction.POOR,
    );
    const fairCount = CREMATION_SATISFACTION_IDS.filter(
      id => formValues[id] === Satisfaction.FAIR,
    ).length;
    const recommendationIsNegative = isNegativeRecommendation(formValues.sv99a);

    return !poorSelected && fairCount < 3 && !recommendationIsNegative;
  };

  // Auto-select or unselect Negative Survey checkbox based on conditions
  const checkAndAutoSelectNegativeSurvey = () => {
    const formValues = getValues();

    if (shouldAutoSelectNegativeSurvey(formValues)) {
      setData(prevData => ({
        ...prevData,
        svNeg: true,
        svPos: false,
        svNeedatt: false,
      }));
    } else if (shouldAutoUncheckNegativeSurvey(formValues)) {
      setData(prevData => ({
        ...prevData,
        svNeg: false,
        svPos: false,
        svNeedatt: false,
      }));
    }
  };

  const submitSurvey = async formData => {
    formData.svRecord = data.svRecord ?? smasterData.smRecord;
    formData.svClient = data.svClient ?? smasterData.smClient;
    formData.svLoc = data.svLoc ?? smasterData.smLoc;
    formData.svGroup = data.svGroup ?? smasterData.smGroup;

    formData.clientId = smasterData.nclientpk;
    formData.groupId = smasterData.ngrouppk;
    formData.locationId = smasterData.locationpk;
    formData.sMasterId = smasterData.npk;

    formData.svPos = data.svPos;
    formData.svNeg = data.svNeg;
    formData.svNeedatt = data.svNeedatt;

    let url = Survey.ADD_CREMATION;
    let successMessage = 'Successfully added survey for ' + smasterData.clientName;
    if (isUpdate) {
      url = Survey.UPDATE_CREMATION + '/' + formData.id;
      successMessage = 'Successfully updated survey for ' + smasterData.clientName;
    }

    setIsSaving(true);

    const response = await post(url, formData);

    if (isMounted.current === false) return;

    setIsSaving(false);

    setToast({
      response: response,
      successMessage: successMessage,
      onSuccess: () => navigate('/surveys/lookup', {state: {serviceType: location?.state?.serviceType}}),
    });
  };

  // The ids must correspond to the data that is sent from api, otherwise defaults and sending data up won't work
  const prompt3 = [
    {prompt: '3a. Response', id: 'sv3a'},
    {prompt: 'b. Transfer', id: 'sv3b'},
    {prompt: 'c. Greet', id: 'sv3c'},
    {prompt: 'd. Care', id: 'sv3d'},
  ];

  const prompt4 = [
    {prompt: '4a. Needs', id: 'sv4a'},
    {prompt: 'b. Wishes', id: 'sv4b'},
    {prompt: 'c. Explain', id: 'sv4c'},
    {prompt: 'd. Detail', id: 'sv4d'},
    {prompt: 'e. Knowledge', id: 'sv4e'},
    {prompt: 'f. Sincere', id: 'sv4f'},
    {prompt: 'g. Answers', id: 'sv4g'},
    {prompt: 'h. Payment', id: 'sv4h'},
  ];

  const promptSupport = [{prompt: 'Support/Follow-up', id: 'sv6k'}];

  const prompt7 = [
    {prompt: '7a. Value', id: 'sv7a'},
    {prompt: '8a. Satisfaction', id: 'sv8a'},
  ];

  const brochure = [
    {key: 'Yes', value: 2},
    {key: 'No', value: 1},
    {key: 'N/A', value: 3},
  ];

  return (
    <>
      {isLoading ? (
        <>
          <LoadingWheel />
          <ErrorHelper toastResponse={toast} />
        </>
      ) : (
        <form onSubmit={handleSubmit(submitSurvey)}>
          <Box w={{base: '100%', lg: '90%'}}>
            <MaintenanceHeader
              register={register}
              control={control}
              errors={errors}
              masterData={smasterData}
              data={data}
              setData={setData}
              reset={reset}
              ids={ids}
              getValues={getValues}
              submit={handleSubmit(submitSurvey)}
            />

            <Flex>
              <Box w="40%">
                <BoxCard textAlign="start">
                  <Flex>
                    <Text mr="4">1.</Text>
                    <Box textAlign="start">
                      <CustomCheckbox name="Health Care" id={'sv1a'} control={control} defaultChecked={data.sv1a} />
                      <CustomCheckbox name="Member" id={'sv1b'} control={control} defaultChecked={data.sv1b} />
                      <CustomCheckbox name="Seminar" id={'sv1d'} control={control} defaultChecked={data.sv1d} />
                      <CustomCheckbox name="Recommendation" id={'sv1c'} control={control} defaultChecked={data.sv1c} />
                    </Box>
                    <Spacer />
                    <Box textAlign="start">
                      <CustomCheckbox name="Internet" id={'sv1e'} control={control} defaultChecked={data.sv1e} />
                      <CustomCheckbox name="Previous Experience" id={'sv1f'} control={control} defaultChecked={data.sv1f} />
                      <CustomCheckbox name="Newspaper" id={'sv1g'} control={control} defaultChecked={data.sv1g} />
                      <CustomCheckbox name="Yellow Pages" id={'sv1h'} control={control} defaultChecked={data.sv1h} />
                    </Box>
                  </Flex>
                  <Textarea rows={1} id="sv1cd" name="Recommendation" {...register('sv1cd')} errors={errors} />
                  <CustomCheckbox name="Other" id={'sv1j'} control={control} defaultChecked={data.sv1j} ml="7" />
                  <Textarea rows={1} id="sv1jd" name="Other" {...register('sv1jd')} errors={errors} />
                </BoxCard>
                <FlexCard>
                  2.
                  <CustomCheckbox name="Price" control={control} id="sv2a" defaultChecked={data.sv2a} />
                  <CustomCheckbox name="Reputation" control={control} id="sv2b" defaultChecked={data.sv2b} />
                  <CustomCheckbox name="Convenience" control={control} id="sv2c" defaultChecked={data.sv2c} />
                  <CustomCheckbox name="Other" control={control} id="sv2d" defaultChecked={data.sv2d} />
                </FlexCard>
                <SatisfactionSection control={control} prompts={prompt3} data={data} onRadioChange={checkAndAutoSelectNegativeSurvey} />
                <SatisfactionSection control={control} prompts={prompt4} data={data} onRadioChange={checkAndAutoSelectNegativeSurvey} />
                <FlexCard textAlign="start">
                  5.
                  <SimpleGrid columns={3} ml="4" gap="3">
                    <CustomCheckbox name="Telephone" control={control} id="sv5a" defaultChecked={data.sv5a} />
                    <CustomCheckbox name="In Person" control={control} id="sv5b" defaultChecked={data.sv5b} />
                    <CustomCheckbox name="Mail" control={control} id="sv5c" defaultChecked={data.sv5c} />
                    <CustomCheckbox name="Online" control={control} id="sv5d" defaultChecked={data.sv5d} />
                    <CustomCheckbox name="Fax" control={control} id="sv5e" defaultChecked={data.sv5e} />
                    <CustomCheckbox name="Other" control={control} id="sv5f" defaultChecked={data.sv5f} />
                  </SimpleGrid>
                </FlexCard>
              </Box>
              <Box w="40%">
                <BoxCard textAlign="start">
                  <Flex>
                    <Text mr="4">6.</Text>
                    <Box>
                      <CustomCheckbox name="Private Viewing" control={control} id="sv6a" defaultChecked={data.sv6a} />
                      <CustomCheckbox name="Family Witnessed Cremation" control={control} id="sv6b" defaultChecked={data.sv6b} />
                      <CustomCheckbox name="Memorial Service in Our Chapel" control={control} id="sv6c" defaultChecked={data.sv6c} />
                      <CustomCheckbox name="Memorial Service at Church / Other Location" control={control} id="sv6d" defaultChecked={data.sv6d} />
                      <CustomCheckbox name="Other" control={control} id="sv6e" defaultChecked={data.sv6e} />
                    </Box>
                  </Flex>
                </BoxCard>

                <SatisfactionSection control={control} prompts={promptSupport} data={data} onRadioChange={checkAndAutoSelectNegativeSurvey} />

                <FlexCard>
                  Urn
                  <CustomCheckbox name="Price" control={control} id="sv6f" defaultChecked={data.sv6f} />
                  <CustomCheckbox name="Material" control={control} id="sv6g" defaultChecked={data.sv6g} />
                  <CustomCheckbox name="Style" control={control} id="sv6h" defaultChecked={data.sv6h} />
                  <CustomCheckbox name="Other" control={control} id="sv6j" defaultChecked={data.sv6j} />
                </FlexCard>

                <SatisfactionSection control={control} prompts={prompt7} data={data} onRadioChange={checkAndAutoSelectNegativeSurvey} />
                <SatisfactionToTen control={control} question={{prompt: '9a. Recommendation', id: 'sv99a'}} data={data} onRadioChange={checkAndAutoSelectNegativeSurvey} />
                <FlexCard>
                  <Text>10. Member</Text>
                  <PreArrangeRadio control={control} id="sv10a" name="10. Member" data={data} />
                </FlexCard>
                <BoxCard>
                  <Flex>
                    <Text>11. Brochure</Text>
                    <CustomIntRadioGroup id="sv11a" control={control} errors={errors} list={brochure} defaultValue={data.sv11a || 2} />
                  </Flex>
                  <CustomInput register={register} errors={errors} name="Name" id="sv11b" />
                  <CustomInput register={register} errors={errors} name="Address 1" id="sv11c" />
                  <CustomInput register={register} errors={errors} name="Address 2" id="sv11d" />
                </BoxCard>
              </Box>

              <Box w="20%">
                <SurveySideBar
                  control={control}
                  includes={{chinese: false, surveyFormat: true, recommendations: true}}
                  data={data}
                  register={register}
                  getValues={getValues}
                  reset={reset}
                  setValue={setValue}
                  errors={errors}
                  svcType={location?.state?.serviceType}
                  sMasterId={location?.state?.id}
                  isSaving={isSaving}
                  setData={setData}
                />
              </Box>
            </Flex>
            <FlexCard>
              <Textarea rows={3} id="svComment" name="svComment" {...register('svComment')} errors={errors} />
            </FlexCard>
          </Box>
          <ErrorHelper toastResponse={toast} />
        </form>
      )}
    </>
  );
};

export default CremationMaintenance;
